'use client';

import { recoverFromChunkError } from '@repo/web-chrome/lib/recover-from-chunk-error';
import { initSentry } from '@repo/web-chrome/lib/sentry';
import * as Sentry from '@sentry/browser';
import { useEffect } from 'react';

interface IGlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

const PAGE_STYLE: React.CSSProperties = {
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '1rem',
  padding: '1.5rem',
  textAlign: 'center',
  fontFamily: 'system-ui, sans-serif',
};

const BUTTON_ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.75rem',
  justifyContent: 'center',
};

const BUTTON_STYLE: React.CSSProperties = {
  padding: '0.5rem 1.25rem',
  borderRadius: '0.5rem',
  border: '1px solid #d4d4d8',
  background: 'transparent',
  fontSize: '0.875rem',
  cursor: 'pointer',
};

const PRIMARY_BUTTON_STYLE: React.CSSProperties = {
  ...BUTTON_STYLE,
  border: '1px solid #18181b',
  background: '#18181b',
  color: '#fafafa',
};

const DIGEST_STYLE: React.CSSProperties = {
  fontSize: '0.75rem',
  color: '#71717a',
  fontFamily: 'ui-monospace, monospace',
};

function hardReload(): void {
  window.location.reload();
}

export default function GlobalError({ error, reset }: IGlobalErrorProps): React.JSX.Element {
  const recovering = recoverFromChunkError(error);

  useEffect(() => {
    if (!recovering) {
      initSentry();
      Sentry.captureException(error);
    }
  }, [error, recovering]);

  if (recovering) {
    return (
      <html lang="en">
        <body style={PAGE_STYLE} />
      </html>
    );
  }

  return (
    <html lang="en">
      <body style={PAGE_STYLE}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>Something went wrong.</h1>
        <p style={{ fontSize: '0.95rem', color: '#52525b', margin: 0, maxWidth: '32rem' }}>
          An unexpected error interrupted the page. Try again, or reload to start fresh.
        </p>
        <div style={BUTTON_ROW_STYLE}>
          <button type="button" style={PRIMARY_BUTTON_STYLE} onClick={reset}>
            Try again
          </button>
          <button type="button" style={BUTTON_STYLE} onClick={hardReload}>
            Reload page
          </button>
        </div>
        {error.digest ? <span style={DIGEST_STYLE}>Error ID: {error.digest}</span> : null}
      </body>
    </html>
  );
}
